import React, { useEffect, useMemo, useState } from 'react';
import { NavBar, NavBarProps } from '../NavBar/NavBar';
import { NavbarEventPropsInterface } from '../../../layout.types';
import chroma from 'chroma-js';
import { withWindow } from '@shared/utils/withWindow';
import { throttle } from 'lodash-es';
import { StyledStickyContainer, StyledContentWrapper } from './StickyNavBar.styles';
import { useStickyNavBarOffset } from './StickyNavBarOffset.context';
import { pxToRem } from '@withjoy/joykit/theme';

interface StickyNavBarProps
  extends Readonly<Omit<NavBarProps, 'variant'>>,
    Readonly<{
      eventProps: NavbarEventPropsInterface;
      pageColor?: string;
      scrollableContainerRef: React.RefObject<HTMLDivElement>;
      isFullWidth?: boolean;
    }> {}

export const StickyNavBar: React.FC<StickyNavBarProps> = ({ pageColor, scrollableContainerRef, isFullWidth, ...navBarProps }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { offset } = useStickyNavBarOffset();

  useEffect(() => {
    const handleScroll = throttle(() => {
      const scrollTop = scrollableContainerRef.current?.scrollTop || 0;
      withWindow(global => setIsVisible(scrollTop > global.innerHeight));
    }, 100);

    const container = scrollableContainerRef.current;

    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }

    return () => undefined;
  }, [scrollableContainerRef]);

  const backgroundColor = useMemo(() => {
    return chroma(pageColor || '#ffffff')
      .alpha(0.8)
      .css();
  }, [pageColor]);

  return (
    <StyledStickyContainer
      backgroundColor={backgroundColor}
      isVisible={isVisible}
      paddingTop={pxToRem(offset)}
    >
      <StyledContentWrapper isFullWidth={isFullWidth}>
        <NavBar
          {...navBarProps}
          variant="inline"
          paddingTop={0}
          isFullWidth={isFullWidth}
        />
      </StyledContentWrapper>
    </StyledStickyContainer>
  );
};
